import { useState, useEffect, useMemo } from 'react';
import { useI18n } from '../../../i18n';
import { C } from '@/lib/design';
import { fetchGlosario, type TerminoGlosario, type CategoriaTermino } from '../services/api';
import { CATEGORIA_STYLES, CATEGORIAS_ORDER } from '../constants/glosario-styles';
import { InfoTooltip } from '@/components/ui/InfoTooltip';

export function GlosarioPage() {
  const { t } = useI18n();
  const g = t.glosario;
  const [terminos, setTerminos] = useState<TerminoGlosario[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [categoria, setCategoria] = useState<CategoriaTermino | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const res = await fetchGlosario();
        if (!cancelled) setTerminos(res.data ?? []);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : g.errorLoading);
          setTerminos([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [g.errorLoading]);

  useEffect(() => {
    const id = window.setTimeout(() => setSearch(searchInput.trim().toLowerCase()), 300);
    return () => window.clearTimeout(id);
  }, [searchInput]);

  const filtered = useMemo(() => {
    const base = categoria ? terminos.filter((x) => x.categoria === categoria) : terminos;
    const q = search;
    if (!q) return [...base].sort((a, b) => a.termino.localeCompare(b.termino));
    return base
      .filter((x) => {
        if (x.termino.toLowerCase().includes(q)) return true;
        if (x.definicion.toLowerCase().includes(q)) return true;
        if (x.sinonimos?.some((s) => s.toLowerCase().includes(q))) return true;
        return false;
      })
      .sort((a, b) => a.termino.localeCompare(b.termino));
  }, [terminos, search, categoria]);

  const conteoPorCategoria = useMemo(() => {
    const map: Record<string, number> = {};
    for (const x of terminos) {
      map[x.categoria] = (map[x.categoria] ?? 0) + 1;
    }
    return map;
  }, [terminos]);

  return (
    <div
      style={{
        padding: '24px 32px',
        background: C.pageBg,
        minHeight: 'calc(100vh - 60px)',
        fontFamily: 'inherit',
      }}
    >
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: 20 }}>
          <h1
            style={{
              margin: 0,
              fontSize: 24,
              fontWeight: 700,
              color: '#0E243E',
              letterSpacing: '-0.01em',
              display: 'inline-flex',
              alignItems: 'center',
            }}
          >
            {g.title}
            <InfoTooltip
              title={t.structure.tooltips.glosario.titulo}
              text={t.structure.tooltips.glosario.ayuda}
            />
          </h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#5A6171' }}>{g.subtitle}</p>
        </div>

        {/* Controls */}
        <div
          style={{
            background: '#fff',
            borderRadius: 10,
            padding: 16,
            marginBottom: 20,
            boxShadow: '0 1px 3px rgba(15,23,42,0.06)',
            border: '1px solid #E5E7EB',
          }}
        >
          <input
            type="text"
            placeholder={g.searchPlaceholder}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 14px',
              border: '1px solid #D1D5DB',
              borderRadius: 8,
              fontSize: 14,
              fontFamily: 'inherit',
              outline: 'none',
              boxSizing: 'border-box',
              marginBottom: 12,
            }}
          />

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
            <ChipButton
              label={`${g.all} (${terminos.length})`}
              active={categoria === null}
              color={C.azul1}
              bg="#EFF6FF"
              onClick={() => setCategoria(null)}
            />
            {CATEGORIAS_ORDER.map((cat) => {
              const style = CATEGORIA_STYLES[cat];
              const count = conteoPorCategoria[cat] ?? 0;
              if (count === 0) return null;
              return (
                <ChipButton
                  key={cat}
                  label={`${g.categoria[cat]} (${count})`}
                  active={categoria === cat}
                  color={style.color}
                  bg={style.bg}
                  onClick={() => setCategoria(cat)}
                />
              );
            })}
          </div>
        </div>

        {/* Counter */}
        <div
          style={{
            fontSize: 12,
            color: '#5A6171',
            marginBottom: 12,
            fontWeight: 500,
          }}
        >
          {filtered.length} {filtered.length === 1 ? g.term : g.terms}
          {(search || categoria) && terminos.length !== filtered.length
            ? ` · ${g.ofTotal(terminos.length)}`
            : ''}
        </div>

        {/* Content */}
        {loading ? (
          <div
            style={{
              textAlign: 'center',
              padding: 60,
              color: '#94A3B8',
              fontSize: 14,
            }}
          >
            {g.loading}
          </div>
        ) : error ? (
          <div
            style={{
              background: '#FEF2F2',
              border: '1px solid #FECACA',
              borderRadius: 8,
              padding: 16,
              color: '#B91C1C',
              fontSize: 13,
            }}
          >
            {error}
          </div>
        ) : filtered.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: 60,
              color: '#94A3B8',
              fontSize: 14,
              background: '#fff',
              borderRadius: 10,
              border: '1px dashed #D1D5DB',
            }}
          >
            {g.empty}
          </div>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(420px, 1fr))',
              gap: 14,
            }}
          >
            {filtered.map((x) => (
              <TerminoCard
                key={x.id}
                termino={x}
                categoriaLabel={g.categoria[x.categoria]}
                synonymsLabel={g.synonyms}
                noTranslateLabel={g.noTranslate}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ChipButton({
  label,
  active,
  color,
  bg,
  onClick,
}: {
  label: string;
  active: boolean;
  color: string;
  bg: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '6px 14px',
        borderRadius: 999,
        border: `1px solid ${active ? color : '#E5E7EB'}`,
        background: active ? color : bg,
        color: active ? '#fff' : color,
        fontSize: 12,
        fontWeight: 600,
        cursor: 'pointer',
        fontFamily: 'inherit',
        transition: 'all 0.15s',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </button>
  );
}

function TerminoCard({
  termino,
  categoriaLabel,
  synonymsLabel,
  noTranslateLabel,
}: {
  termino: TerminoGlosario;
  categoriaLabel: string;
  synonymsLabel: string;
  noTranslateLabel: string;
}) {
  const style = CATEGORIA_STYLES[termino.categoria] ?? CATEGORIA_STYLES.GENERAL;
  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 10,
        padding: '16px 18px',
        border: '1px solid #E5E7EB',
        boxShadow: '0 1px 3px rgba(15,23,42,0.05)',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <h3
          style={{
            margin: 0,
            fontSize: 15,
            fontWeight: 700,
            color: '#0E243E',
            lineHeight: 1.3,
          }}
        >
          {termino.termino}
        </h3>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0, flexWrap: 'wrap' }}>
          {termino.noTraducir && (
            <span
              title={noTranslateLabel}
              style={{
                padding: '2px 8px',
                borderRadius: 6,
                background: '#FEF3C7',
                color: '#92400E',
                fontSize: 10,
                fontWeight: 600,
                whiteSpace: 'nowrap',
              }}
            >
              {noTranslateLabel}
            </span>
          )}
          <span
            style={{
              padding: '2px 8px',
              borderRadius: 6,
              background: style.bg,
              color: style.color,
              fontSize: 10,
              fontWeight: 700,
              whiteSpace: 'nowrap',
              textTransform: 'uppercase',
              letterSpacing: '0.03em',
            }}
          >
            {categoriaLabel}
          </span>
        </div>
      </div>

      <p
        style={{
          margin: 0,
          fontSize: 13,
          color: '#374151',
          lineHeight: 1.55,
          whiteSpace: 'pre-wrap',
        }}
      >
        {termino.definicion}
      </p>

      {termino.sinonimos && termino.sinonimos.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 5,
            borderTop: '1px solid #F3F4F6',
            paddingTop: 8,
          }}
        >
          <span style={{ fontSize: 11, color: '#94A3B8', fontWeight: 600 }}>{synonymsLabel}</span>
          {termino.sinonimos.map((s, i) => (
            <span
              key={i}
              style={{
                padding: '1px 8px',
                borderRadius: 4,
                background: '#F3F4F6',
                color: '#4B5563',
                fontSize: 11,
                fontWeight: 500,
              }}
            >
              {s}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
